import useAnalytics from 'common/hooks/useAnalytics'
import SystemAnnouncementDialog from 'modules/announcement/components/SystemAnnouncementDialog'
import { useUserInfo } from 'modules/user/api/queries'
import { selectedUserInfoState } from 'modules/user/stores/atom'
import * as React from 'react'
import { Outlet } from 'react-router'
import { useSetRecoilState } from 'recoil'
import { normalizeRoleForAnalytics } from 'utils/analytics'

import Box from '@mui/material/Box'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import Navbar from './Navbar'
import Sidebar from './Sidebar'

const APP_BAR_MOBILE = 50
const APP_BAR_DESKTOP = 70

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100vh',
  backgroundColor: theme.palette.grey[100],
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 10,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}))

export type ScreenType = 'desktop' | 'tablet' | 'mobile'

const DashboardLayout: React.FC = () => {
  const theme = useTheme()
  const setUserInfo = useSetRecoilState(selectedUserInfoState)
  const { data: userInfo } = useUserInfo()
  const { identify, setUserProperty } = useAnalytics()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'))
  const screenType: ScreenType = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile'
  const [desktopOpen, setDesktopOpen] = React.useState(true)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const open = isDesktop ? desktopOpen : mobileOpen
  const handleSetOpen = React.useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (value) => {
      if (isDesktop) {
        setDesktopOpen(value)
      } else {
        setMobileOpen(value)
      }
    },
    [isDesktop]
  )

  React.useEffect(() => {
    setUserInfo(userInfo)
  }, [userInfo, setUserInfo])

  React.useEffect(() => {
    if (!userInfo) return
    identify(userInfo.id)
    setUserProperty('farm_role', normalizeRoleForAnalytics(userInfo.user_role?.name) ?? null)
    setUserProperty('account_status', userInfo.status ?? null)
    setUserProperty('org_id', userInfo.organization_id ?? null)
  }, [userInfo, identify, setUserProperty])

  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar open={open} setOpen={handleSetOpen} screenType={screenType} />
      <Sidebar open={open} setOpen={handleSetOpen} screenType={screenType} />
      <MainStyle>
        <Outlet />
      </MainStyle>
      <SystemAnnouncementDialog />
    </Box>
  )
}

export default DashboardLayout
